import { appSettings } from '../AppSettings';
import { Sample } from './SampleData';
import { BaseGet, BasePost, BuildArrayQueryParams } from './BaseData';
import { uniq } from 'lodash';
import { Transition, TransitionAndTransitionSample } from './SampleTrackingTransitionData';
import { TransitionSample } from './SampleTrackingData';
import { SupplementalFile } from './SupplementalFileData';

export interface SampleJourneyHierarchy {
  root: Sample;
  hierarchy: ReadonlyArray<SampleTransformationDescendant>;
}

export interface SampleTransformationDescendant {
  ancestor: Sample;
  descendant: Sample;
  depth: number;
}

export interface SampleJourneyTree {
  ancestor: Sample;
  descendant: ReadonlyArray<SampleJourneyTree>;
  depth: number;
  path: string[];
}

export interface SampleJourneyTransitionInformation {
  transitionCreatedIn?: Transition;
  transitionSampleCreatedIn?: TransitionSample;
  currentTransition?: Transition;
  currentTransitionSample?: TransitionSample;
  sampleId: string;
}

export interface SampleSequencingJourneyDetails {
  fastQs?: SequencingFastQ[];
  fastqLocation?: string;
  informaticsInstrumentId?: string;
  informaticsLibraryTypeId?: string;
  libraryKitId?: string;
  libraryKitName?: string;
  sampleId: string;
  sequenceRunId?: string;
  sequenceType?: string;
  sequencingLabId?: string;
  sequencingLabName?: string;
  sequencingPlatformId?: string;
  sequencingPlatformName?: string;
}

export interface SequencingFastQ {
  file: SupplementalFile;
  type: { name: 'r1' | 'r2' };
}

export async function GetHierarchyForSampleId(
  sampleId: string,
  accessToken: string,
  decrypt: boolean = false
): Promise<ReadonlyArray<SampleJourneyHierarchy>> {
  return await BaseGet(
    `${appSettings.api.endpoint}/api/v2/SampleJourney/Hierarchy/${sampleId}/?decrypt=${decrypt}`,
    accessToken
  );
}

export async function GetSampleJourneyTransitionInformation(
  sampleIds: ReadonlyArray<string>,
  accessToken?: string
): Promise<ReadonlyArray<SampleJourneyTransitionInformation>> {
  return await BaseGet(
    `${
      appSettings.api.endpoint
    }/api/v2/SampleJourney/SampleTransformation/SampleTransitionInformation?${BuildArrayQueryParams(
      sampleIds,
      'sampleIds'
    )}`,
    accessToken
  );
}
export async function GetAllTransitionAndTransitionSamples(
  sampleIds: ReadonlyArray<string>,
  accessToken?: string
): Promise<ReadonlyArray<TransitionAndTransitionSample>> {
  return await BaseGet(
    `${appSettings.api.endpoint}/api/v2/SampleJourney/TransitionSamples?${BuildArrayQueryParams(
      sampleIds,
      'sampleIds'
    )}`,
    accessToken
  );
}

export async function GetSampleSequencingJourneyDetails(
  sampleIds: ReadonlyArray<string>,
  accessToken?: string
): Promise<ReadonlyArray<SampleSequencingJourneyDetails>> {
  return await BasePost(
    `${appSettings.api.endpoint}/api/v2/SampleJourney/SequencingJourneyDetails`,
    sampleIds,
    accessToken
  );
}

export function ConvertToSampleJourneyTree(hierarchy: SampleJourneyHierarchy): SampleJourneyTree {
  const { root, hierarchy: transformationDescendants } = hierarchy;

  // Create a map of each sample to its descendants and depth
  const descendantsMap = new Map<string, Array<{ descendant: Sample; depth: number }>>();
  for (const { ancestor, descendant, depth } of transformationDescendants) {
    const ancestorId = ancestor.sampleId;
    if (!descendantsMap.has(ancestorId)) {
      descendantsMap.set(ancestorId, []);
    }
    descendantsMap.get(ancestorId)?.push({ descendant, depth });
  }

  // Recursively build the SampleJourneyTree
  function buildTree(sample: Sample, path: string[], depth: number): SampleJourneyTree {
    const sampleId = sample.sampleId;
    const descendants = descendantsMap.get(sampleId) ?? [];
    return {
      ancestor: sample,
      descendant: descendants.map(({ descendant }) => buildTree(descendant, [...path, sample.sampleId], depth + 1)),
      depth,
      path: [...path, sample.sampleId],
    };
  }

  return buildTree(root, [], 0);
}

export function GetSampleSpecificBranch(
  tree: SampleJourneyTree,
  sampleIds: ReadonlyArray<string>
): SampleJourneyTree | undefined {
  if (sampleIds.includes(tree.ancestor.sampleId)) {
    return tree;
  }

  for (const subTree of tree.descendant) {
    const node = GetSampleSpecificBranch(subTree, sampleIds);
    if (node) {
      return {
        ancestor: tree.ancestor,
        descendant: [node],
        depth: tree.depth,
        path: tree.path,
      };
    }
  }

  return undefined;
}

export function GetAllSampleIds(trees: SampleJourneyTree[]): ReadonlyArray<string> {
  function getIds(tree: SampleJourneyTree) {
    const nodes = [tree.ancestor.sampleId];

    for (const child of tree.descendant) {
      nodes.push(...getIds(child));
    }

    return nodes;
  }

  return uniq(trees.flatMap(tree => getIds(tree)));
}
